import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { i18n } from '@kostbar/i18n'
import { setUser as setSentryUser } from '@kostbar/monitoring'
import type { User } from '../types'
import { authApi } from '@/api/endpoints/auth'

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: User | null
  isAuthenticated: boolean

  // Actions
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  refreshAccessToken: () => Promise<void>
  loadUser: () => Promise<void>
  hasRole: (role: string) => boolean
  updateLocale: (locale: string) => Promise<void>
}

// Single-flight refresh: parallel 401s (several queries on one screen) must share one token
// rotation. The refresh token is single-use server-side, so racing it makes the loser present
// an already-rotated token — which used to log the whole session out.
let refreshInFlight: Promise<void> | null = null

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        const response = await authApi.login(email, password)
        set({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          isAuthenticated: true,
        })
        // Load user profile after successful login
        await get().loadUser()
      },

      logout: () => {
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        })
        // Clear user context from error tracking
        setSentryUser(null)
      },

      refreshAccessToken: async () => {
        if (!refreshInFlight) {
          refreshInFlight = (async () => {
            const { refreshToken } = get()
            if (!refreshToken) {
              throw new Error('No refresh token available')
            }

            const response = await authApi.refresh(refreshToken)
            set({
              accessToken: response.accessToken,
              refreshToken: response.refreshToken,
              isAuthenticated: true,
            })
          })().finally(() => {
            refreshInFlight = null
          })
        }
        return refreshInFlight
      },

      loadUser: async () => {
        const meResponse = await authApi.me()
        // Normalize backend roles like "ROLE_TENANT_ADMIN" to frontend roles like "TENANT_ADMIN"
        const normalizedRoles = meResponse.roles.map((r: string) =>
          r.startsWith('ROLE_') ? r.substring(5) : r,
        )
        const locale = meResponse.locale ?? 'de'
        const user = {
          email: meResponse.email,
          roles: normalizedRoles,
          tenantId: meResponse.tenantId,
          restaurantIds: meResponse.restaurantIds,
          locale,
        }
        set({ user })
        // Set user context for error tracking
        setSentryUser({
          email: user.email,
          id: user.tenantId,
        })
        // Sync i18n language with user's locale from backend
        if (i18n.language !== locale) {
          await i18n.changeLanguage(locale)
        }
        // Also update localStorage to keep it in sync
        localStorage.setItem('preferred-language', locale)
      },

      hasRole: (role: string) => {
        const { user } = get()
        const normalized =
          role && role.startsWith('ROLE_') ? role.substring(5) : role
        return user?.roles.includes(normalized) ?? false
      },

      updateLocale: async (locale: string) => {
        // Update backend first
        await authApi.updatePreferences({ locale })
        // Update local state
        const { user } = get()
        if (user) {
          set({ user: { ...user, locale } })
        }
        // Sync i18n
        await i18n.changeLanguage(locale)
        // Update localStorage as fallback
        localStorage.setItem('preferred-language', locale)
      },
    }),
    {
      name: 'auth-storage',
      // Only persist tokens, not user object (to avoid stale data)
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      // Hydrate isAuthenticated based on whether tokens exist
      onRehydrateStorage: () => (state) => {
        if (!state) return
        if (state.accessToken && state.refreshToken) {
          state.isAuthenticated = true
        }
      },
    },
  ),
)
